<script setup lang="ts">
import { VButton, VIcon } from '@/modules/shared/components'
import { ref, onMounted, onUnmounted, nextTick } from 'vue'

///////////////////////////////////////////////////////////////////////////////
// Dynamic dashed lines
///////////////////////////////////////////////////////////////////////////////

const containerRef = ref<HTMLElement | null>(null)
const cardRef = ref<HTMLElement | null>(null)
const avatarRefs = ref<HTMLElement[]>([])
const lines = ref<string[]>([])

const setAvatarRef = (el: any) => {
  if (el && !avatarRefs.value.includes(el)) {
    avatarRefs.value.push(el)
  }
}

const updateLines = () => {
  if (!containerRef.value || !cardRef.value) return

  const container = containerRef.value.getBoundingClientRect()
  const card = cardRef.value.getBoundingClientRect()

  // Card center & edges relative to container
  const cardCx = card.left - container.left + card.width / 2
  const cardCy = card.top - container.top + card.height / 2
  const cardLeft = card.left - container.left
  const cardRight = cardLeft + card.width
  const cardTop = card.top - container.top
  const cardBottom = cardTop + card.height

  const paths: string[] = []

  avatarRefs.value.forEach((avatar) => {
    const rect = avatar.getBoundingClientRect()
    const ax = rect.left - container.left + rect.width / 2
    const ay = rect.top - container.top + rect.height / 2

    // Target: nearest point on card edge
    let tx: number, ty: number
    if (ax < cardLeft) {
      tx = cardLeft
      ty = Math.max(cardTop, Math.min(cardBottom, cardCy + (ay - cardCy) * 0.4))
    } else if (ax > cardRight) {
      tx = cardRight
      ty = Math.max(cardTop, Math.min(cardBottom, cardCy + (ay - cardCy) * 0.4))
    } else if (ay < cardTop) {
      tx = Math.max(cardLeft, Math.min(cardRight, cardCx + (ax - cardCx) * 0.4))
      ty = cardTop
    } else {
      tx = Math.max(cardLeft, Math.min(cardRight, cardCx + (ax - cardCx) * 0.4))
      ty = cardBottom
    }

    // Control point: keep avatar's horizontal position but move vertical toward card
    // This creates a gentle arc that stays close to the avatar's side
    const cpx = ax
    const cpy = ty

    paths.push(`M ${ax} ${ay} Q ${cpx} ${cpy} ${tx} ${ty}`)
  })

  lines.value = paths
}

let resizeObserver: ResizeObserver | null = null

onMounted(async () => {
  await nextTick()
  updateLines()

  if (containerRef.value) {
    resizeObserver = new ResizeObserver(() => updateLines())
    resizeObserver.observe(containerRef.value)
  }
})

onUnmounted(() => {
  resizeObserver?.disconnect()
})
</script>

<template>
  <div class="space-y-6">
    <!-- Page Header -->
    <div class="flex items-start justify-between">
      <div>
        <div class="mb-2 text-xs font-semibold uppercase tracking-widest text-[#3b88af]">Connections by Venture360</div>
        <h1 class="mb-2 text-3xl font-bold leading-tight text-gray-800 dark:text-white">
          Your access to investors <br /><span class="italic text-[#3b88af]">starts here.</span>
        </h1>
        <p class="max-w-md text-sm leading-relaxed text-gray-500 dark:text-gray-400">
          We match you with investors that align with your goals based on your investor profile. Your connections will
          appear below once matched.
        </p>
      </div>
      <RouterLink :to="{ name: 'connecting.groups.edit' }">
        <VButton size="sm" variant="v-blue">
          <span class="flex items-center gap-1.5">
            <VIcon name="Edit" class="h-3.5 w-3.5" />
            Edit Profile
          </span>
        </VButton>
      </RouterLink>
    </div>

    <!-- Central Waiting Card with decorative background -->
    <div
      ref="containerRef"
      class="relative flex min-h-[420px] items-center justify-center overflow-hidden rounded-2xl bg-white dark:bg-zinc-900 md:border md:border-gray-200 md:shadow-md md:dark:border-zinc-700"
    >
      <!-- Dynamic dashed lines -->
      <svg class="pointer-events-none absolute inset-0 hidden h-full w-full md:block">
        <path
          v-for="(d, i) in lines"
          :key="i"
          :d="d"
          fill="none"
          stroke="#d0dce8"
          stroke-width="1.5"
          stroke-dasharray="6 5"
        />
      </svg>

      <!-- Ghost avatars -->
      <div :ref="setAvatarRef" class="ghost-avatar hidden md:flex" style="top: 5%; left: 20%">
        <VIcon name="User" class="h-5 w-5 text-gray-300" />
      </div>
      <div :ref="setAvatarRef" class="ghost-avatar hidden md:flex" style="top: 5%; right: 20%">
        <VIcon name="User" class="h-5 w-5 text-gray-300" />
      </div>
      <div :ref="setAvatarRef" class="ghost-avatar hidden md:flex" style="top: 40%; left: 4%">
        <VIcon name="User" class="h-5 w-5 text-gray-300" />
      </div>
      <div :ref="setAvatarRef" class="ghost-avatar ghost-avatar-sm hidden md:flex" style="top: 68%; left: 6%">
        <VIcon name="User" class="h-4 w-4 text-gray-300" />
      </div>
      <div :ref="setAvatarRef" class="ghost-avatar hidden md:flex" style="top: 31%; right: 5%">
        <VIcon name="User" class="h-5 w-5 text-gray-300" />
      </div>
      <div :ref="setAvatarRef" class="ghost-avatar ghost-avatar-sm hidden md:flex" style="top: 71%; right: 7%">
        <VIcon name="User" class="h-4 w-4 text-gray-300" />
      </div>
      <div :ref="setAvatarRef" class="ghost-avatar ghost-avatar-sm hidden md:flex" style="bottom: 6%; left: 18%">
        <VIcon name="User" class="h-4 w-4 text-gray-300" />
      </div>
      <div :ref="setAvatarRef" class="ghost-avatar hidden md:flex" style="bottom: 5%; right: 22%">
        <VIcon name="User" class="h-5 w-5 text-gray-300" />
      </div>

      <!-- Center content card -->
      <div
        ref="cardRef"
        class="relative z-10 my-10 flex max-w-[440px] flex-col items-center rounded-2xl border-[1.5px] border-gray-200 bg-white px-11 py-9 shadow-[0_8px_32px_rgba(29,52,71,0.1)]"
      >
        <!-- Pulsing Icon -->
        <div class="relative mb-6">
          <div class="pulse-ring absolute inset-0 rounded-full border-2 border-[#3b88af]/15"></div>
          <div class="pulse-ring pulse-ring-2 absolute inset-0 rounded-full border-2 border-[#3b88af]/15"></div>
          <div class="relative z-10 flex h-20 w-20 items-center justify-center rounded-full bg-[#3b88af]/[0.08]">
            <VIcon name="Users" class="h-8 w-8 text-[#3b88af]" />
          </div>
        </div>

        <div class="mb-2 text-sm font-semibold text-[#3b88af]">Venture360 is finding your matches</div>
        <h2 class="mb-3 text-center text-2xl font-bold text-gray-800 dark:text-white">
          We're connecting you with<br />investors
        </h2>
        <p class="mb-8 max-w-sm text-center text-sm leading-relaxed text-gray-500 dark:text-gray-400">
          Don't worry — none of your information is shared with anyone unless you approve the connection first.
        </p>

        <RouterLink :to="{ name: 'connecting.groups.edit' }">
          <VButton size="md" variant="v-blue" class="!rounded-full !px-6"> Edit Profile </VButton>
        </RouterLink>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes pulse-ring {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.6);
    opacity: 0;
  }
}

.pulse-ring {
  animation: pulse-ring 1.8s ease-out infinite;
}

.pulse-ring-2 {
  animation-delay: 1.2s;
}

.ghost-avatar {
  position: absolute;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #f3f4f6;
  border: 1.5px solid #e5e7eb;
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

@media (min-width: 768px) {
  .ghost-avatar {
    display: flex;
  }
}

@media (max-width: 1000px) {
  .ghost-avatar {
    transform: scale(0.85);
  }
  .ghost-avatar[style*='left: 20%'] {
    left: 10% !important;
  }
  .ghost-avatar[style*='right: 20%'] {
    right: 10% !important;
  }
  .ghost-avatar[style*='left: 4%'] {
    left: 1% !important;
  }
  .ghost-avatar[style*='left: 6%'] {
    left: 2% !important;
  }
  .ghost-avatar[style*='right: 5%'] {
    right: 1% !important;
  }
  .ghost-avatar[style*='right: 7%'] {
    right: 2% !important;
  }
  .ghost-avatar[style*='left: 18%'] {
    left: 8% !important;
  }
  .ghost-avatar[style*='right: 22%'] {
    right: 10% !important;
  }
}

.ghost-avatar-sm {
  width: 38px;
  height: 38px;
}
</style>
